import React from "react";
import HotelRow from "./HotelRow";
import SortableTableHeader from "../HotelPage/SortableTableHeader";

/**
 * `statsPendingIds` (Set d'ids) remplace le drapeau global `loadingStats` : seules les
 * lignes dont les stats sont réellement en vol affichent un placeholder. Un drapeau global
 * re-rendait les 83 lignes à chaque bascule et vidait toutes les colonnes quand on
 * rafraîchissait un seul hôtel. `loadingStats` reste accepté en repli.
 */
const HotelsTable = ({ hotels, onUpdateStatus, onGenerateSnapshot, onEditDescription, onEditHotel, sortState, onSort, loadingStats = false, statsPendingIds = null, unifiedHotelLink, onUpdateFeatureStatus, onUpdateTag = null, isGeneralList = false, loading = false }) => {
  if (hotels.length === 0) {
    // Les en-têtes dépendent des features présentes dans `hotels` : on ne peut pas les
    // rendre fidèlement à vide. On se contente donc du bon message.
    return (
      <div className="bg-white shadow-md rounded-lg p-8 text-center">
        <div className="text-gray-500 text-lg">{loading ? "Chargement des hôtels..." : "Aucun hôtel disponible pour le moment."}</div>
      </div>
    );
  }

  // Déterminer quelles colonnes afficher selon les features présentes dans la liste
  const hasAnyLuniShower = hotels.some((h) => h.has_lunishower);
  const hasAnyGreenMiles = hotels.some((h) => h.has_greenmiles);
  const hasAnyLuniButton = hotels.some((h) => h.has_lunibutton);

  const isStatsLoading = (hotel) => (statsPendingIds ? statsPendingIds.has(hotel.id) : loadingStats);

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableTableHeader column="name" currentColumn={sortState.column} currentDirection={sortState.direction} onSort={onSort} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nom de l'hôtel
              </SortableTableHeader>

              <SortableTableHeader column="tag" currentColumn={sortState.column} currentDirection={sortState.direction} onSort={onSort} className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tag
              </SortableTableHeader>

              {/* Dans la liste générale, chaque feature a sa propre colonne */}
              {isGeneralList ? (
                <>
                  {hasAnyLuniShower && <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">LuniShower</th>}
                  {hasAnyGreenMiles && <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GreenMiles</th>}
                  {hasAnyLuniButton && <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">LuniButton</th>}
                </>
              ) : (
                // Dans les listes filtrées, une seule colonne "Statuts Features"
                <SortableTableHeader column="type" currentColumn={sortState.column} currentDirection={sortState.direction} onSort={onSort} className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statuts Features
                </SortableTableHeader>
              )}

              {/* Colonnes spécifiques aux features (uniquement dans les listes filtrées) */}
              {!isGeneralList && hasAnyLuniShower && (
                <>
                  <SortableTableHeader column="performance" currentColumn={sortState.column} currentDirection={sortState.direction} onSort={onSort} className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    % OK
                    <br />
                    <span className="text-gray-400 normal-case">(Performance)</span>
                  </SortableTableHeader>

                  <SortableTableHeader column="room_status" currentColumn={sortState.column} currentDirection={sortState.direction} onSort={onSort} className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statuts
                  </SortableTableHeader>
                </>
              )}

              <SortableTableHeader column="room_count" currentColumn={sortState.column} currentDirection={sortState.direction} onSort={onSort} className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nb de Chambres
              </SortableTableHeader>

              {isGeneralList && (
                <SortableTableHeader column="installation_date" currentColumn={sortState.column} currentDirection={sortState.direction} onSort={onSort} className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date d'installation
                </SortableTableHeader>
              )}

              {/* Colonnes spécifiques aux features (uniquement dans les listes filtrées) */}
              {!isGeneralList && hasAnyGreenMiles && (
                <SortableTableHeader column="nb_greenmiles_stays" currentColumn={sortState.column} currentDirection={sortState.direction} onSort={onSort} className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Séjours GM
                </SortableTableHeader>
              )}

              {!isGeneralList && hasAnyLuniShower && (
                <SortableTableHeader column="last_check" currentColumn={sortState.column} currentDirection={sortState.direction} onSort={onSort} className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activité
                </SortableTableHeader>
              )}

              <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {hotels.map((hotel) => (
              <HotelRow key={hotel.id} hotel={hotel} onUpdateStatus={onUpdateStatus} onGenerateSnapshot={onGenerateSnapshot} onEditDescription={onEditDescription} onEditHotel={onEditHotel} loadingStats={isStatsLoading(hotel)} onUpdateFeatureStatus={onUpdateFeatureStatus} onUpdateTag={onUpdateTag} showLuniShowerColumns={!isGeneralList && hasAnyLuniShower} showGreenMilesColumns={!isGeneralList && hasAnyGreenMiles} isGeneralList={isGeneralList} hasAnyLuniShower={hasAnyLuniShower} hasAnyGreenMiles={hasAnyGreenMiles} hasAnyLuniButton={hasAnyLuniButton} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HotelsTable;
